import React, { useCallback } from "react";
import { useNotification } from "../../hooks/useNotification";
import { useSearchParams } from "react-router-dom";

import { IAnimal, IBatch, IFlock } from "../../modules/animals/interface";
import {
  deleteAnimals,
  deleteBatchs,
  deleteFlocks,
  moveAnimalsToOtherBatch,
  updateAnimal,
} from "../../services/animals";
// import { ControlCheckbox as Checkbox } from '../Checkbox';
import { useTranslate } from "../../hooks/useTranslate";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import AnimalTab from "./animal-tab";
import BatchTab from "./batch-tab";
import FlockTab from "./flock-tab";

interface Props {
  loading?: boolean;
  allAnimals: IAnimal[];
  batchs: IBatch[];
  flocks: IFlock[];
  cb: () => Promise<void>;
}

const AnimalsManagement = ({
  loading,
  batchs,
  flocks,
  allAnimals,
  cb,
}: Props) => {
  const { successNotification, errorNotification } = useNotification();
  const { translate } = useTranslate();

  const onDeleteAnimals = useCallback(
    async (ids: string[]) => {
      try {
        await deleteAnimals(ids);
        await cb();
        successNotification(translate("management.remove_animals_success"));
      } catch {
        errorNotification(translate("management.remove_animals_error"));
      }
    },
    [cb, errorNotification, successNotification, translate]
  );

  const onDeleteBatchs = useCallback(
    async (ids: string[]) => {
      try {
        await deleteBatchs(ids);
        await cb();
        successNotification(translate("management.remove_batch_success"));
      } catch {
        errorNotification(translate("management.remove_batch_error"));
      }
    },
    [errorNotification, cb, successNotification, translate]
  );

  const onDeleteFlocks = useCallback(
    async (ids: string[]) => {
      try {
        await deleteFlocks(ids);
        await cb();
        successNotification(translate("management.remove_flock_success"));
      } catch {
        errorNotification(translate("management.remove_flock_error"));
      }
    },
    [errorNotification, cb, successNotification, translate]
  );

  const animalMoveBatchs = useCallback(
    async (batch: IBatch, animals: IAnimal[]) => {
      try {
        await Promise.all(
          animals.map(
            async (a) => await moveAnimalsToOtherBatch(a.id, batch.id)
          )
        );
        await cb();
        successNotification(translate("management.change_batch_success"));
      } catch {
        errorNotification(translate("management.change_batch_error"));
      }
    },
    [cb, errorNotification, successNotification, translate]
  );

  const addAnimalsBatch = useCallback(
    async (batch: IBatch, ids: string[]) => {
      try {
        await Promise.all(
          ids.map(async (id) => {
            const form = new FormData();
            form.append("batch", batch.id);
            await updateAnimal(id, form);
          })
        );
        successNotification(
          translate("animals.title_add_animal_batch_success")
        );
        await cb();
      } catch {
        errorNotification(translate("animals.title_add_animal_batch_error"));
      }
    },
    [cb, errorNotification, successNotification, translate]
  );

  const addAnimalsFlock = useCallback(
    async (flock: IFlock, ids: string[]) => {
      try {
        await Promise.all(
          ids.map(async (id) => {
            const form = new FormData();
            form.append("flock", flock.id);
            await updateAnimal(id, form);
          })
        );
        successNotification(
          translate("animals.title_add_animal_flock_success")
        );
        await cb();
      } catch {
        errorNotification(translate("animals.title_add_animal_flock_error"));
      }
    },
    [cb, errorNotification, successNotification, translate]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  return (
    <>
      <Tabs
        indicatorColor="secondary"
        color="secondary"
        value={searchParams.get("active") ?? null}
        variant="fullWidth"
        onChange={(_, value) => {
          if (value === null) {
            searchParams.delete("active");
          } else {
            searchParams.set("active", value);
          }
          setSearchParams(searchParams);
        }}
        sx={(theme) => ({ mx: 2 })}
      >
        <Tab
          color="secondary"
          sx={(theme) => ({
            "&.Mui-selected": {
              color: theme.palette.secondary.main,
            },
          })}
          value={null}
          label={
            <Stack alignItems="center" direction="row" spacing={1}>
              <span>{`${translate("management.all_animals")}`}</span>
              <Chip label={`${allAnimals.length}`} size="small" />
            </Stack>
          }
        />
        <Tab
          value={"batch"}
          label={
            <Stack alignItems="center" direction="row" spacing={1}>
              <span>{`${translate("management.batch")}`}</span>
              <Chip label={`${batchs.length}`} size="small" />
            </Stack>
          }
        />
        <Tab
          value={"flock"}
          label={
            <Stack alignItems="center" direction="row" spacing={1}>
              <span>{`${translate("management.flock")}`}</span>
              <Chip label={`${flocks.length}`} size="small" />
            </Stack>
          }
        />
      </Tabs>
      <Paper elevation={3}>
        <Collapse in={loading} unmountOnExit>
          <Box
            sx={{ p: 3 }}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <CircularProgress size={24} />
          </Box>
        </Collapse>
        <Collapse in={!searchParams.has("active")} unmountOnExit>
          <AnimalTab animals={allAnimals} onDeleted={onDeleteAnimals} />
        </Collapse>
        <Collapse in={searchParams.get("active") === "batch"} unmountOnExit>
          <BatchTab
            animals={allAnimals}
            batchs={batchs}
            onDeleted={onDeleteBatchs}
            onAnimalsMoved={animalMoveBatchs}
            onAddAnimals={addAnimalsBatch}
            onAnimalDeleted={onDeleteAnimals}
          />
        </Collapse>
        <Collapse in={searchParams.get("active") === "flock"} unmountOnExit>
          <FlockTab
            animals={allAnimals}
            batchs={batchs}
            flocks={flocks}
            onDeleted={onDeleteFlocks}
            onAnimalsMoved={animalMoveBatchs}
            onAddAnimals={addAnimalsFlock}
            onAnimalDeleted={onDeleteAnimals}
          />
        </Collapse>
      </Paper>
    </>
  );
};

export { AnimalsManagement };
